/**
 * Cart items - handles quantity changes and removal inside the cart drawer.
 *
 * Talks directly to Shopify's Cart Ajax API:
 *   POST /cart/change.js → updates a line item by index, then dispatches
 *   CartUpdateEvent so the parent drawer can refresh its HTML.
 *
 * Public API:
 *   <cart-items-component section-id="...">
 *     .changeQuantity(line, quantity)
 *
 * @see https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-change-js
 */

import { Component } from "@theme/component";
import { CartUpdateEvent, CartErrorEvent } from "@theme/events";

class CartItemsComponent extends Component {
  /**
   * Updates the quantity of a line item.
   *
   * @param {number} line - 1-based line index in the cart
   * @param {number|string} quantity - target quantity (0 to remove)
   * @returns {Promise<void>}
   */
  async changeQuantity(line, quantity) {
    const qty = Math.max(0, parseInt(quantity, 10) || 0);
    this.#setLoading(true);

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ line, quantity: qty }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.description || body.message || `HTTP ${response.status}`);
      }

      // Notify the rest of the page (drawer, header bubble, etc.)
      document.dispatchEvent(new CartUpdateEvent());
    } catch (error) {
      console.error("[cart-items] update failed:", error);
      document.dispatchEvent(new CartErrorEvent("cart-items", error.message));
    } finally {
      this.#setLoading(false);
    }
  }

  /**
   * Toggles a visual "loading" state on the drawer wrapper while the request runs.
   * Prevents double-clicks and gives the user feedback.
   * @param {boolean} loading
   */
  #setLoading(loading) {
    const wrapper = this.closest("[data-cart-drawer-inner]");
    if (wrapper) wrapper.dataset.loading = loading ? "true" : "false";
  }
}

if (!customElements.get("cart-items-component")) {
  customElements.define("cart-items-component", CartItemsComponent);
}
